import { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Container, Main, Flex } from '../../../components/styled/Layout';
import { Card, CardHeader, CardTitle, CardContent } from '../../../components/styled/Card';
import { SecondaryButton, PrimaryButton, DangerButton } from '../../../components/styled/Button';
import LoadingOverlay from '../../../components/LoadingOverlay';
import hestonService from '../services/hestonService';
import '../styles/ProjectDetail.css';

export default function ProjectPrices() {
    const { projectId } = useParams();
    const navigate = useNavigate();

    const [project, setProject] = useState(null);
    const [addresses, setAddresses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [simulating, setSimulating] = useState(false);
    const [resetting, setResetting] = useState(false);
    const [error, setError] = useState(null);
    const [stats, setStats] = useState(null);
    const [confirmReset, setConfirmReset] = useState(false);

    const loadProjectData = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const [projectData, addressData] = await Promise.all([
                hestonService.getProject(projectId),
                hestonService.listAddresses(projectId)
            ]);
            setProject(projectData);
            setAddresses(addressData);
            calculateStats(addressData);
        } catch (err) {
            console.error('Error loading project:', err);
            setError('Failed to load project data. ' + (err.message || 'Unknown error'));
        } finally {
            setLoading(false);
        }
    }, [projectId]);

    useEffect(() => {
        loadProjectData();
    }, [loadProjectData]);

    const calculateStats = (addrList) => {
        let simulatedCount = 0;
        let maxChange = 0;
        addrList.forEach(addr => {
            if (addr.isSimulated) simulatedCount++;
            ['pest365', 'pest365Plus', 'oneTime', 'initialServiceVisit', 'mosquitoTickControl'].forEach(field => {
                const current = parseFloat(String(addr[field] || '0').replace(/[$,]/g, ''));
                const original = parseFloat(String(addr[`original_${field}`] || '0').replace(/[$,]/g, ''));
                if (current > 0 && original > 0) {
                    const change = Math.abs((current - original) / original);
                    if (change > maxChange) maxChange = change;
                }
            });
        });
        setStats({ simulatedCount, maxChange: (maxChange * 100).toFixed(2) });
    };

    const handleSimulate = async (type = 'random') => {
        try {
            setSimulating(true);
            const result = await hestonService.simulatePrices(projectId, type);
            if (result.jobId) {
                pollSimulationStatus(result.jobId);
            } else {
                await loadProjectData();
                setSimulating(false);
            }
        } catch (err) {
            setError('Simulation failed: ' + (err.message || 'Unknown error'));
            setSimulating(false);
        }
    };

    const pollSimulationStatus = async (jobId) => {
        try {
            const status = await hestonService.getSimulationStatus(projectId, jobId);
            if (status.status === 'completed') {
                await loadProjectData();
                setSimulating(false);
            } else if (status.status === 'failed') {
                setError('Simulation failed: ' + (status.error || 'Unknown error'));
                setSimulating(false);
            } else {
                setTimeout(() => pollSimulationStatus(jobId), 2000);
            }
        } catch (err) {
            setTimeout(() => pollSimulationStatus(jobId), 3000);
        }
    };

    const handleReset = async () => {
        if (!confirmReset) {
            setConfirmReset(true);
            setTimeout(() => setConfirmReset(false), 4000);
            return;
        }
        try {
            setResetting(true);
            setConfirmReset(false);
            const result = await hestonService.resetPrices(projectId);
            if (result.jobId) {
                pollResetStatus(result.jobId);
            } else {
                await loadProjectData();
                setResetting(false);
            }
        } catch (err) {
            setError('Reset failed: ' + (err.message || 'Unknown error'));
            setResetting(false);
        }
    };

    const pollResetStatus = async (jobId) => {
        try {
            const status = await hestonService.getResetStatus(projectId, jobId);
            if (status.status === 'completed') {
                await loadProjectData();
                setResetting(false);
            } else if (status.status === 'failed') {
                setError('Reset failed: ' + (status.error || 'Unknown error'));
                setResetting(false);
            } else {
                setTimeout(() => pollResetStatus(jobId), 2000);
            }
        } catch (err) {
            setTimeout(() => pollResetStatus(jobId), 3000);
        }
    };

    const PriceCell = ({ current, original }) => {
        if (!current && !original) return <span className="text-muted">-</span>;
        if (!original) return <span>{hestonService.formatCurrency(current)}</span>;
        const currVal = parseFloat(String(current).replace(/[$,]/g, '')) || 0;
        const origVal = parseFloat(String(original).replace(/[$,]/g, '')) || 0;
        if (currVal === origVal) return <span>{hestonService.formatCurrency(current)}</span>;
        const diff = currVal - origVal;
        const color = diff > 0 ? '#27ae60' : '#c0392b';
        return (
            <div style={{ fontSize: '0.9em' }}>
                <div style={{ fontWeight: 'bold' }}>{hestonService.formatCurrency(current)}</div>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                    <span style={{ color, fontSize: '0.85em' }}>{diff > 0 ? "▲" : "▼"}</span>
                    <span style={{ color: '#999', textDecoration: 'line-through', fontSize: '0.85em' }}>{hestonService.formatCurrency(original)}</span>
                </div>
            </div>
        );
    };

    if (loading && !project) return <LoadingOverlay message="Loading simulator..." />;

    if (error) {
        return (
            <Container>
                <Main>
                    <Card variant="outlined" style={{ borderColor: '#C0392B', background: '#F8D7DA' }}>
                        <CardContent>
                            <h3 style={{ color: '#721C24', margin: '0 0 12px 0' }}>Error</h3>
                            <p style={{ color: '#721C24', margin: '0 0 16px 0' }}>{error}</p>
                            <SecondaryButton onClick={() => navigate(`/heston/projects/${projectId}`)}>
                                ← Back to Dashboard
                            </SecondaryButton>
                        </CardContent>
                    </Card>
                </Main>
            </Container>
        );
    }

    return (
        <Container>
            <Main>
                <Flex direction="column" gap="var(--space-5)" align="stretch">
                    <Flex justify="space-between" align="flex-start" wrap="wrap" gap="var(--space-3)">
                        <div>
                            <SecondaryButton onClick={() => navigate(`/heston/projects/${projectId}`)} style={{ marginBottom: '12px' }}>← Back to Dashboard</SecondaryButton>
                            <Flex gap="var(--space-2)" align="center">
                                <h1 style={{ fontSize: '24px', fontWeight: 'bold', color: '#2C3E50', margin: 0 }}>Pricing Simulator</h1>
                                <span style={{ background: '#3498db', color: 'white', padding: '2px 8px', borderRadius: '12px', fontSize: '12px', fontWeight: 'bold' }}>BETA v4</span>
                            </Flex>
                        </div>
                        <Flex gap="var(--space-2)">
                            <PrimaryButton onClick={() => handleSimulate("random")} disabled={simulating || resetting}>🎲 Randomize</PrimaryButton>
                            <PrimaryButton onClick={() => handleSimulate("heston")} disabled={simulating || resetting} style={{ background: '#8e44ad', borderColor: '#8e44ad' }}>📈 Heston Prices</PrimaryButton>
                            <DangerButton onClick={handleReset} disabled={simulating || resetting || stats?.simulatedCount === 0}>{resetting ? "Resetting..." : confirmReset ? "⚠️ Confirm" : "← Back to Certus"}</DangerButton>
                        </Flex>
                    </Flex>
                    <Card>
                        <CardHeader><CardTitle>Pricing Impact Analysis</CardTitle></CardHeader>
                        <CardContent style={{ padding: 0 }}>
                            <div className="address-table-container">
                                <table className="address-table">
                                    <thead>
                                        <tr>
                                            <th>Address</th>
                                            <th>City</th>
                                            <th className="text-right">Pest365</th>
                                            <th className="text-right">Pest365+</th>
                                            <th className="text-right">One-Time</th>
                                            <th className="text-right">Initial Visit</th>
                                            <th className="text-right">Mosquito Tick</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {addresses.map(addr => (
                                            <tr key={addr.addressId}>
                                                <td>{addr.address}</td>
                                                <td>{addr.city}</td>
                                                <td className="text-right"><PriceCell current={addr.pest365} original={addr.original_pest365} /></td>
                                                <td className="text-right"><PriceCell current={addr.pest365Plus} original={addr.original_pest365Plus} /></td>
                                                <td className="text-right"><PriceCell current={addr.oneTime} original={addr.original_oneTime} /></td>
                                                <td className="text-right"><PriceCell current={addr.initialServiceVisit} original={addr.original_initialServiceVisit} /></td>
                                                <td className="text-right"><PriceCell current={addr.mosquitoTickControl} original={addr.original_mosquitoTickControl} /></td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </CardContent>
                    </Card>
                </Flex>
            </Main>
            {(simulating || resetting) && <LoadingOverlay message={resetting ? "Resetting..." : "Simulating..."} />}
        </Container>
    );
}
